import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus,
  Pencil, 
  Trash2,
  Eye,
  Search,
  RefreshCw,
  Filter,
  ArrowLeft,
  MoreHorizontal,
  Book,
  School,
  GraduationCap,
  Clock,
  Video,
  Users,
  BookOpen,
  LayoutGrid,
  LayoutList,
  Ban,
  CheckCircle,
  Monitor,
  MapPin,
  ChevronDown,
  Settings,
  TrendingUp,
  BarChart3,
  Grid3X3,
  List,
  Activity,
  Download,
  Upload,
  Layers,
  Star
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Course, CourseFamily, CourseMode } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { usePermissions, MODULE_IDS } from '@/contexts/PermissionContext';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";

// Fix for the type issue with courses returned from supabase
type CourseWithFamilies = Omit<Course, 'mode'> & {
  mode: string | string[];
  display_id?: string;
  course_families: {
    name: string;
  };
};

type ViewMode = 'grid' | 'list';

interface CourseSummary {
  totalCourses: number;
  activeCourses: number;
  inactiveCourses: number;
  familyCount: number;
}

const Courses = () => {
  const navigate = useNavigate();
  const { hasPermissionById, loading: permissionsLoading } = usePermissions();
  const [courses, setCourses] = useState<CourseWithFamilies[]>([]);
  const [courseFamilies, setCourseFamilies] = useState<CourseFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [codeFilter, setCodeFilter] = useState<string>('_all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('courses');
  const [showInactive, setShowInactive] = useState(false);
  const [summaryData, setSummaryData] = useState<CourseSummary>({
    totalCourses: 0,
    activeCourses: 0,
    inactiveCourses: 0,
    familyCount: 0
  });
  
  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isInactivateDialogOpen, setIsInactivateDialogOpen] = useState(false);
  const [isReactivateDialogOpen, setIsReactivateDialogOpen] = useState(false);
  const [currentCourse, setCurrentCourse] = useState<Course | null>(null);
  
  // Form states
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRequirements, setFormRequirements] = useState('');
  const [formFamilyId, setFormFamilyId] = useState<string | undefined>(undefined);
  const [formMode, setFormMode] = useState<CourseMode>('onlineAsync');
  const [formDurationOnlineAsync, setFormDurationOnlineAsync] = useState<number | undefined>(undefined);
  const [formDurationOnlineSync, setFormDurationOnlineSync] = useState<number | undefined>(undefined);
  const [formDurationInPerson, setFormDurationInPerson] = useState<number | undefined>(undefined);
  const [formDurationMinutes, setFormDurationMinutes] = useState<number | undefined>(undefined);
  const [formLevel, setFormLevel] = useState<string | undefined>(undefined);
  const [formLearningGoals, setFormLearningGoals] = useState('');
  const [formCoverUrl, setFormCoverUrl] = useState('');
  const [formThumbnailUrl, setFormThumbnailUrl] = useState('');

  // Check user permissions
  const canCreateCourse = hasPermissionById(MODULE_IDS.COURSES, 'create');
  const canUpdateCourse = hasPermissionById(MODULE_IDS.COURSES, 'update');
  const canDeleteCourse = hasPermissionById(MODULE_IDS.COURSES, 'delete');

  useEffect(() => {
    fetchCourses();
    fetchCourseFamilies();
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [statusFilter]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('courses')
        .select('*, course_families(name)')
        .order('name');

      // Filter by status if not 'all'
      if (statusFilter === 'active') {
        query = query.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        query = query.eq('is_active', false);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setCourses(data as CourseWithFamilies[]);
      
      // Update summary data
      updateSummaryData(data as CourseWithFamilies[]);
    } catch (error) {
      console.error('Erro ao buscar cursos:', error);
      toast.error('Não foi possível carregar os cursos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseFamilies = async () => {
    try {
      const { data, error } = await supabase
        .from('course_families')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setCourseFamilies(data);
      
      // Update family count in summary
      setSummaryData(prev => ({
        ...prev,
        familyCount: data?.length || 0
      }));
    } catch (error) {
      console.error('Erro ao buscar famílias de cursos:', error);
      toast.error('Não foi possível carregar as famílias de cursos');
    }
  };

  const updateSummaryData = (courseData: CourseWithFamilies[]) => {
    const total = courseData.length;
    const active = courseData.filter(course => course.is_active !== false).length;
    const inactive = courseData.filter(course => course.is_active === false).length;
    
    setSummaryData(prev => ({
      ...prev,
      totalCourses: total,
      activeCourses: active,
      inactiveCourses: inactive
    }));
  };

  const handleSearch = () => {
    if (!searchTerm && (!codeFilter || codeFilter === "_all")) {
      fetchCourses();
      return;
    }

    let filtered = [...courses];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(course => 
        course.name.toLowerCase().includes(term) ||
        (course.description && course.description.toLowerCase().includes(term))
      );
    }
    
    if (codeFilter && codeFilter !== "_all") {
      filtered = filtered.filter(course => 
        course.course_family_id === codeFilter
      );
    }
    
    setCourses(filtered);
  };
  
  const resetFilters = () => {
    setSearchTerm('');
    setCodeFilter('_all');
    setStatusFilter('all');
    fetchCourses();
  };

  const handleEditCourse = (course: CourseWithFamilies) => {
    navigate(`/courses/${course.id}/edit`);
  };

  const openDeleteDialog = (course: CourseWithFamilies) => {
    setCurrentCourse(course as unknown as Course);
    setIsDeleteDialogOpen(true);
  };

  const openInactivateDialog = (course: CourseWithFamilies) => {
    setCurrentCourse(course as unknown as Course);
    setIsInactivateDialogOpen(true);
  };

  const openReactivateDialog = (course: CourseWithFamilies) => {
    setCurrentCourse(course as unknown as Course);
    setIsReactivateDialogOpen(true);
  };

  const handleDeleteCourse = async () => {
    if (!currentCourse) return;

    try {
      // Check if there are any classes associated with this course
      const { count: classesCount, error: countError } = await supabase
        .from('classes')
        .select('*', { count: 'exact', head: true })
        .eq('course_id', currentCourse.id);
      
      if (countError) throw countError;
      
      if (classesCount && classesCount > 0) {
        toast.error(`Não é possível excluir este curso pois existem ${classesCount} formações associadas a ele`);
        setIsDeleteDialogOpen(false);
        return;
      }

      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', currentCourse.id);

      if (error) throw error;

      toast.success('Curso excluído com sucesso');
      setIsDeleteDialogOpen(false);
      fetchCourses();
    } catch (error) {
      console.error('Erro ao excluir curso:', error);
      toast.error('Não foi possível excluir o curso');
    }
  };

  const handleInactivateCourse = async () => {
    if (!currentCourse) return;

    try {
      const { error } = await supabase
        .from('courses')
        .update({ is_active: false })
        .eq('id', currentCourse.id);

      if (error) throw error;

      toast.success('Curso inativado com sucesso');
      setIsInactivateDialogOpen(false);
      setCurrentCourse(null);
      fetchCourses();
    } catch (error) {
      console.error('Erro ao inativar curso:', error);
      toast.error('Não foi possível inativar o curso');
    }
  };

  const handleReactivateCourse = async () => {
    if (!currentCourse) return;

    try {
      const { error } = await supabase
        .from('courses')
        .update({ is_active: true })
        .eq('id', currentCourse.id);

      if (error) throw error;

      toast.success('Curso reativado com sucesso');
      setIsReactivateDialogOpen(false);
      setCurrentCourse(null);
      fetchCourses();
    } catch (error) {
      console.error('Erro ao reativar curso:', error);
      toast.error('Não foi possível reativar o curso');
    }
  };

  const renderDurationByMode = (course: CourseWithFamilies) => {
    if (Array.isArray(course.mode)) {
      const durations = [];
      
      if (course.mode.includes('onlineAsync') && course.duration_onlineasync) {
        durations.push(`Assínc: ${course.duration_onlineasync} min`);
      }
      
      if (course.mode.includes('onlineSync') && course.duration_onlinesync) {
        durations.push(`Sínc: ${course.duration_onlinesync} min`);
      }
      
      if (course.mode.includes('in-person') && course.duration_inperson) {
        durations.push(`Pres: ${course.duration_inperson} min`);
      }
      
      if (durations.length > 0) {
        return durations.join(' | ');
      }
      
      return course.duration_minutes ? `${course.duration_minutes} min` : '-';
    }
    
    switch (course.mode) {
      case 'onlineAsync':
        return course.duration_onlineasync ? `${course.duration_onlineasync} min` : '-';
      case 'onlineSync':
        return course.duration_onlinesync ? `${course.duration_onlinesync} min` : '-';
      case 'in-person':
        return course.duration_inperson ? `${course.duration_inperson} min` : '-';
      default:
        return course.duration_minutes ? `${course.duration_minutes} min` : '-';
    }
  };

  const getModeLabel = (mode: string | string[]) => {
    if (Array.isArray(mode)) {
      return mode.map(m => getModeLabel(m)).join(' / ');
    }
    
    switch (mode) {
      case 'onlineAsync':
        return 'Online Assíncrono';
      case 'onlineSync':
        return 'Online Síncrono';
      case 'in-person':
        return 'Presencial';
      default:
        return mode;
    }
  };

  const handleCreateCourse = () => {
    navigate('/courses/new');
  };

  const getModeBadges = (mode: string | string[]) => {
    const modes = Array.isArray(mode) ? mode : [mode];
    
    return modes.map((singleMode, index) => {
      switch (singleMode) {
        case 'onlineAsync':
          return (
            <Badge 
              key={index}
              variant="outline" 
              className="bg-[#3b65f5]/10 text-[#3b65f5] hover:bg-[#3b65f5]/20 border-[#3b65f5]/20 font-extrabold text-xs"
            >
              <Monitor className="h-3 w-3 mr-1" />
              A
            </Badge>
          );
        case 'onlineSync':
          return (
            <Badge 
              key={index}
              variant="outline" 
              className="bg-[#ff4a8f]/10 text-[#ff4a8f] hover:bg-[#ff4a8f]/20 border-[#ff4a8f]/20 font-extrabold text-xs"
            >
              <Users className="h-3 w-3 mr-1" />
              S
            </Badge>
          );
        case 'in-person':
          return (
            <Badge 
              key={index}
              variant="outline" 
              className="bg-[#c8e600]/10 text-[#c8e600] hover:bg-[#c8e600]/20 border-[#c8e600]/20 font-extrabold text-xs"
            >
              <MapPin className="h-3 w-3 mr-1" />
              P
            </Badge>
          );
        default:
          return (
            <Badge key={index} variant="outline" className="font-extrabold text-xs">
              {singleMode}
            </Badge>
          );
      }
    });
  };

    const getCourseCard = (course: CourseWithFamilies) => {
    return (
      <Card className="group relative h-full overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        {/* Imagem do curso (thumbnail ou capa) */}
        <div className="relative aspect-video bg-gradient-to-br from-[#3b65f5]/10 to-[#ff4a8f]/10 dark:from-[#3b65f5]/20 dark:to-[#ff4a8f]/20 overflow-hidden">
          {course.thumbnail_url || course.cover_url ? (
            <img 
              src={course.thumbnail_url || course.cover_url} 
              alt={course.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#3b65f5]/10 via-[#ff4a8f]/5 to-[#c8e600]/10 dark:from-[#3b65f5]/20 dark:via-[#ff4a8f]/20 dark:to-[#c8e600]/20">
              <BookOpen className="h-12 w-12 text-[#3b65f5] dark:text-[#3b65f5]/80" />
            </div>
          )}
          
          {/* Label da categoria sobre a imagem */}
          <div className="absolute top-3 left-3">
            <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-extrabold shadow-sm">
              {course.course_families?.name || 'Geral'}
            </Badge>
          </div>
        </div>

        <CardContent className="p-4 space-y-3">
          {/* Título do curso */}
          <h3 className="font-extrabold text-sm text-gray-900 dark:text-white line-clamp-2 leading-tight">
            {course.name}
          </h3>

          {/* Descrição do curso */}
          {course.description && (
            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
              {course.description}
            </p>
          )}

          {/* Informações do curso */}
          <div className="flex items-center justify-between">
            {/* Badges de modalidade */}
            <div className="flex gap-1 flex-wrap">
              {getModeBadges(course.mode)}
            </div>
            
            {/* Ícone de status */}
            <div className="flex items-center justify-center w-6 h-6">
              {course.is_active !== false ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <Ban className="h-5 w-5 text-red-500" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Modern Header with Background Image */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundColor: '#1F1746',
          backgroundImage: 'url(/elemento-01.svg)',
          backgroundSize: '200%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Cursos</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Gerencie e organize todos os cursos disponíveis para formações
                </p>
              </div>
            </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="default" 
                onClick={() => navigate('/courses/import')} 
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
              >
                <Upload className="h-4 w-4 mr-2" />
                Importar Cursos
              </Button>
              {canCreateCourse && (
                <Button
                  size="default" 
                  onClick={handleCreateCourse}
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Curso
                </Button>
              )}
            </div>
            </div>
          </div>
        </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">
        {/* Enhanced Summary Cards with Better Visual Hierarchy */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <BookOpen className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Total de Cursos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.totalCourses}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Em {summaryData.familyCount} famílias
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-blue-500" />
                  <span className="text-xs text-green-600 font-extrabold">+8%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Cursos Ativos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.activeCourses}
                </div>
              </div>
              <div className="space-y-2">
                <Progress 
                  value={summaryData.totalCourses > 0 ? (summaryData.activeCourses / summaryData.totalCourses) * 100 : 0} 
                  className="h-1.5"
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {summaryData.totalCourses > 0 
                      ? `${Math.round((summaryData.activeCourses / summaryData.totalCourses) * 100)}% do total` 
                      : 'Nenhum curso'}
              </div>
                  <div className="flex items-center gap-1">
                    <Activity className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600 font-extrabold">+3%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-red-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-red-100 rounded-md">
                    <Ban className="h-4 w-4 text-red-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Cursos Inativos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.inactiveCourses}
                </div>
              </div>
              <div className="space-y-2">
                <Progress 
                  value={summaryData.totalCourses > 0 ? (summaryData.inactiveCourses / summaryData.totalCourses) * 100 : 0} 
                  className="h-1.5"
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {summaryData.totalCourses > 0 
                      ? `${Math.round((summaryData.inactiveCourses / summaryData.totalCourses) * 100)}% do total` 
                      : 'Nenhum curso'}
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-red-500 rotate-180" />
                    <span className="text-xs text-red-600 font-extrabold">-1%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <Layers className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Famílias</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.familyCount}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Categorias disponíveis
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-purple-500" />
                  <span className="text-xs text-purple-600 font-extrabold">+2</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Main Content with Modern Design */}
        <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6">
            {/* Enhanced Filter Section - All in one line */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 flex-1">
                <div className="min-w-0 flex-1">
                <Select value={codeFilter} onValueChange={setCodeFilter}>
                    <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Família de Cursos" />
                  </SelectTrigger>
                  <SelectContent>
                      <SelectItem value="_all" className="py-3">
                        <div>
                          <div className="font-extrabold">Todas as Famílias</div>
                          <div className="text-xs text-gray-500">Mostrar todos os cursos</div>
                        </div>
                      </SelectItem>
                      {courseFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id} className="py-3">
                          <div>
                            <div className="font-extrabold">{family.name}</div>
                            <div className="text-xs text-gray-500 truncate max-w-48">
                              Filtrar por esta família
                            </div>
                          </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>

                <div className="relative min-w-0 flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
                  <Input
                    placeholder="Pesquisar cursos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
              </div>

                <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-10 px-4 border-gray-200 hover:bg-gray-50">
                      <Filter className="h-4 w-4 mr-2" />
                      Filtros
                      {(showInactive) && (
                        <Badge variant="secondary" className="ml-2 h-4 w-4 p-0 text-xs">
                          1
                        </Badge>
                      )}
                      <ChevronDown className="h-3 w-3 ml-2 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-6" align="end">
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-extrabold text-gray-900 mb-1">Filtros Avançados</h4>
                        <p className="text-sm text-gray-500">Personalize a visualização dos cursos</p>
            </div>
            
                      <Separator />
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <label className="text-sm font-extrabold text-gray-700">
                              Mostrar cursos inativos
                            </label>
                            <p className="text-xs text-gray-500">
                              Incluir cursos desativados na listagem
                            </p>
                          </div>
                          <Checkbox 
                            checked={showInactive} 
                            onCheckedChange={(checked) => setShowInactive(!!checked)} 
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-between pt-4 border-t">
                  <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setSearchTerm('');
                            setCodeFilter('_all');
                            setShowInactive(false);
                          }}
                        >
                          Limpar Filtros
                  </Button>
                        <Button size="sm" onClick={() => setIsFilterOpen(false)}>
                          Aplicar
                  </Button>
              </div>
              </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('courses')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'courses'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="h-4 w-4" />
                  Cursos
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'analytics'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" />
                  Análises
                </button>
            </div>
        </div>

            {/* Content Area */}
            {courses.length > 0 ? (
              <div className="space-y-6">
                {activeTab === 'courses' && (
                  <div className="space-y-6">
                    {/* View Mode Toggle */}
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        {courses.filter(course => showInactive || course.is_active !== false).length} cursos encontrados
                      </div>
                      <div className="flex items-center gap-1 border rounded-md p-0.5">
                        <Button 
                          variant={viewMode === 'grid' ? "default" : "ghost"} 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => setViewMode('grid')}
                        >
                          <LayoutGrid className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          variant={viewMode === 'list' ? "default" : "ghost"} 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => setViewMode('list')}
                        >
                          <LayoutList className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    {loading ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[...Array(8)].map((_, i) => (
                          <Card key={i} className="overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                            <div className="relative aspect-video bg-gray-200 dark:bg-gray-700">
                              <Skeleton className="w-full h-full" />
                              <div className="absolute top-3 left-3">
                                <Skeleton className="h-6 w-16 rounded-full" />
                              </div>
                            </div>
                            <CardContent className="p-4 space-y-3">
                              <Skeleton className="h-5 w-3/4" />
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-2/3" />
                              </div>
                              <div className="flex justify-between items-center">
                                <Skeleton className="h-6 w-20" />
                                <Skeleton className="h-5 w-5 rounded-full" />
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : viewMode === 'grid' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {courses
                          .filter(course => showInactive || course.is_active !== false)
                          .map(course => (
                            <div 
                              key={course.id} 
                              className="cursor-pointer"
                              onClick={() => navigate(`/courses/${course.id}`)}
                            >
                              {getCourseCard(course)}
                            </div>
                          ))}
                      </div>
                    ) : (
                      <Card className="border shadow-sm bg-gradient-to-br from-white to-gray-50">
                        <CardContent className="p-0">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Nome</TableHead>
                                <TableHead>Família</TableHead>
                                <TableHead>Modalidade</TableHead>
                                <TableHead>Duração</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {courses
                                .filter(course => showInactive || course.is_active !== false)
                                .map(course => (
                                  <TableRow 
                                    key={course.id} 
                                    className="cursor-pointer hover:bg-accent/50"
                                    onClick={() => navigate(`/courses/${course.id}`)}
                                  >
                                    <TableCell>
                                      <div className="flex items-center space-x-2">
                                        <Avatar className="h-8 w-8 border">
                                          {course.thumbnail_url ? (
                                            <AvatarImage src={course.thumbnail_url} alt={course.name} />
                                          ) : (
                                            <AvatarFallback className="bg-primary/10 text-primary">
                                              <BookOpen className="h-4 w-4" />
                                            </AvatarFallback>
                                          )}
                                        </Avatar>
                                        <div className="font-extrabold">{course.name}</div>
                                      </div>
                                    </TableCell>
                                    <TableCell>{courseFamilies.find(f => f.id === course.course_family_id)?.name || '-'}</TableCell>
                                    <TableCell>
                                      <div className="flex gap-1 flex-wrap">
                                        {getModeBadges(course.mode)}
                                      </div>
                                    </TableCell>
                                    <TableCell>{renderDurationByMode(course)}</TableCell>
                                    <TableCell>
                                      <Badge 
                                        variant={course.is_active !== false ? "default" : "destructive"}
                                        className="text-xs"
                                      >
                                        {course.is_active !== false ? "Ativo" : "Inativo"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex justify-end gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/courses/${course.id}`);
                                          }}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        
                                        {canUpdateCourse && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditCourse(course);
                                            }}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                        )}
                                        
                                        {canDeleteCourse && course.is_active !== false && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-[#ff4a8f]"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openInactivateDialog(course);
                                            }}
                                          >
                                            <Ban className="h-4 w-4" />
                                          </Button>
                                        )}

                                        {canDeleteCourse && course.is_active === false && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-green-600"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openReactivateDialog(course);
                                            }}
                                          >
                                            <CheckCircle className="h-4 w-4" />
                                          </Button>
                                        )}

                                        {canDeleteCourse && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-destructive"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openDeleteDialog(course);
                                            }}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
                
                {activeTab === 'analytics' && (
                  <Card className="border shadow-sm bg-gradient-to-br from-white to-gray-50">
                    <CardContent className="p-8">
                      <div className="text-center py-16">
                        <div className="space-y-4">
                          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                            <BarChart3 className="h-10 w-10 text-gray-400" />
                          </div>
                          <div className="space-y-2">
                            <h3 className="text-lg font-extrabold text-gray-900">
                              Análises em Desenvolvimento
                            </h3>
                            <p className="text-gray-500 max-w-md mx-auto">
                              Em breve você terá acesso a análises detalhadas sobre seus cursos, incluindo estatísticas de uso e performance.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="space-y-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <BookOpen className="h-12 w-12 text-blue-500" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-xl font-extrabold text-gray-900">
                      Nenhum curso encontrado
                    </h3>
                    <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                      Para começar, crie cursos para organizar suas formações de forma eficiente.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {canCreateCourse && (
                      <Button onClick={handleCreateCourse} size="lg">
                        <Plus className="h-5 w-5 mr-2" />
                        Criar Primeiro Curso
                      </Button>
                    )}
                    <Button variant="outline" size="lg">
                      <Download className="h-5 w-5 mr-2" />
                      Importar Dados
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
        {/* Bottom Spacing */}
        <div className="h-16" />

        {/* Dialog de confirmação de exclusão */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente o curso
              <span className="font-extrabold"> {currentCourse?.name}</span> e todos os dados associados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCourse} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isInactivateDialogOpen} onOpenChange={setIsInactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar Curso</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja inativar o curso
              <span className="font-extrabold"> {currentCourse?.name}</span>? 
              O curso ficará indisponível para novas formações, mas as formações existentes não serão afetadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleInactivateCourse} className="bg-[#ff4a8f] text-white hover:bg-[#ff4a8f]/90">
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isReactivateDialogOpen} onOpenChange={setIsReactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reativar Curso</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja reativar o curso
              <span className="font-extrabold"> {currentCourse?.name}</span>? 
              O curso ficará disponível novamente para novas formações.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReactivateCourse} className="bg-[#c8e600] text-white hover:bg-[#c8e600]/90">
              Reativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Courses; 